import { useState } from 'react';
import type { PublicUser, OAuthScope } from '@runninglime/shared';
import { apiPost } from '../../lib/api.ts';

const ASSIGNABLE_PERMISSIONS: { scope: OAuthScope; label: string; description: string }[] = [
  { scope: 'admin:products', label: 'Products', description: 'Manage product catalogue' },
  { scope: 'admin:orders', label: 'Orders', description: 'View and manage orders' },
  { scope: 'admin:tickets', label: 'Tickets', description: 'Handle support tickets' },
  { scope: 'admin:invites', label: 'Invites', description: 'Create and manage invite tokens' },
  { scope: 'admin:users', label: 'Users', description: 'View and manage users' },
  { scope: 'admin:enrollment', label: 'Enrollment', description: 'Enroll new users' },
];

interface EnrollmentResult {
  user: PublicUser;
  generatedPassword: string;
}

export default function EnrollUser() {
  const [type, setType] = useState<'customer' | 'employee'>('customer');

  // Common fields
  const [email, setEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [contactName, setContactName] = useState('');

  // Billing/Shipping
  const [vatNumber, setVatNumber] = useState('');
  const [sdiNumber, setSdiNumber] = useState('');
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [province, setProvince] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [country, setCountry] = useState('IT');

  // Employee permissions
  const [permissions, setPermissions] = useState<OAuthScope[]>([]);

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<EnrollmentResult | null>(null);
  const [copied, setCopied] = useState(false);

  const togglePermission = (scope: OAuthScope) => {
    setPermissions((prev) =>
      prev.includes(scope) ? prev.filter((p) => p !== scope) : [...prev, scope]
    );
  };

  const hasBillingData = vatNumber || sdiNumber || street || city || province || postalCode;

  const resetForm = () => {
    setEmail('');
    setCompanyName('');
    setContactName('');
    setVatNumber('');
    setSdiNumber('');
    setStreet('');
    setCity('');
    setProvince('');
    setPostalCode('');
    setCountry('IT');
    setPermissions([]);
    setError('');
    setResult(null);
    setCopied(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setResult(null);

    if (!email || !companyName || !contactName) {
      setError('Email, company name, and contact name are required.');
      return;
    }

    // Build billing profile
    let billingProfile: Record<string, unknown> | undefined;
    if (type === 'customer') {
      // Required for customer
      if (!vatNumber || !sdiNumber || !street || !city || !province || !postalCode || !country) {
        setError('All billing and shipping fields are required for customer enrollment.');
        return;
      }
      billingProfile = {
        vatNumber,
        sdiNumber,
        shippingAddress: { street, city, province, postalCode, country },
      };
    } else if (hasBillingData) {
      // Optional for employee — include only if partially filled
      billingProfile = {
        vatNumber,
        sdiNumber,
        shippingAddress: { street, city, province, postalCode, country },
      };
    }

    setLoading(true);
    try {
      const res = await apiPost<EnrollmentResult>('/admin/enrollment', {
        type,
        email,
        companyName,
        contactName,
        billingProfile,
        permissions: type === 'employee' ? permissions : undefined,
      });

      if (res.success && res.data) {
        setResult(res.data);
      } else {
        setError(res.error || 'Enrollment failed');
      }
    } catch {
      setError('An unexpected error occurred');
    }
    setLoading(false);
  };

  const copyCredentials = async () => {
    if (!result) return;
    const text = `Email: ${result.user.email}\nTemporary Password: ${result.generatedPassword}`;
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // ── Success Screen ──
  if (result) {
    return (
      <div className="animate-fade-in max-w-2xl">
        <h1 className="text-3xl font-bold text-dark mb-2">User Enrolled Successfully</h1>
        <p className="text-gray-500 mb-6">
          The {type} account has been created and auto-approved. Share the credentials below with the user.
        </p>

        <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Name</p>
              <p className="font-medium text-dark">{result.user.contactName}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Company</p>
              <p className="font-medium text-dark">{result.user.companyName}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Role</p>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                type === 'employee' ? 'bg-indigo-100 text-indigo-800' : 'bg-emerald-100 text-emerald-800'
              }`}>
                {type === 'employee' ? 'Employee' : 'Customer'}
              </span>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Status</p>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                Verified (auto-approved)
              </span>
            </div>
          </div>

          {type === 'employee' && result.user.permissions && result.user.permissions.length > 0 && (
            <div className="mb-6">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Permissions</p>
              <div className="flex flex-wrap gap-1.5">
                {result.user.permissions.map((p) => (
                  <span key={p} className="px-2 py-1 bg-indigo-50 text-indigo-700 rounded-md text-xs font-medium">
                    {ASSIGNABLE_PERMISSIONS.find((ap) => ap.scope === p)?.label || p}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <p className="text-xs text-amber-700 uppercase tracking-wider font-semibold mb-3 flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
              </svg>
              Login Credentials (share with user)
            </p>
            <div className="space-y-2">
              <div>
                <p className="text-xs text-gray-500">Email</p>
                <p className="font-mono text-sm text-dark font-medium">{result.user.email}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Temporary Password</p>
                <p className="font-mono text-sm text-dark font-medium break-all">{result.generatedPassword}</p>
              </div>
            </div>
            <p className="text-xs text-amber-600 mt-3">
              The user will be required to change this password on first login.
            </p>
          </div>
        </div>

        <div className="flex gap-3">
          <button
            onClick={copyCredentials}
            className="px-5 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-medium text-dark hover:bg-gray-50 transition-colors flex items-center gap-2"
          >
            {copied ? (
              <>
                <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
                Copied!
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184" />
                </svg>
                Copy Credentials
              </>
            )}
          </button>
          <button
            onClick={resetForm}
            className="btn-gradient px-5 py-2.5 rounded-xl text-sm font-semibold"
          >
            Enroll Another User
          </button>
        </div>
      </div>
    );
  }

  // ── Enrollment Form ──
  return (
    <div className="animate-fade-in max-w-3xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-dark">User Enrollment</h1>
        <p className="text-gray-500 mt-1">Create a new customer or employee account with auto-approval.</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
            {error}
          </div>
        )}

        {/* ─── User Type Toggle ─── */}
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-dark mb-4">Account Type</h2>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setType('customer')}
              className={`flex-1 px-4 py-3 rounded-xl text-sm font-medium border-2 transition-all ${
                type === 'customer'
                  ? 'border-lime bg-lime/10 text-dark'
                  : 'border-gray-200 text-gray-500 hover:border-gray-300'
              }`}
            >
              <div className="text-center">
                <div className="text-lg mb-1">👤</div>
                <div className="font-semibold">Customer</div>
                <div className="text-xs text-gray-400 mt-1">Store access, billing required</div>
              </div>
            </button>
            <button
              type="button"
              onClick={() => setType('employee')}
              className={`flex-1 px-4 py-3 rounded-xl text-sm font-medium border-2 transition-all ${
                type === 'employee'
                  ? 'border-indigo-400 bg-indigo-50 text-dark'
                  : 'border-gray-200 text-gray-500 hover:border-gray-300'
              }`}
            >
              <div className="text-center">
                <div className="text-lg mb-1">🛡️</div>
                <div className="font-semibold">Employee</div>
                <div className="text-xs text-gray-400 mt-1">Admin powers, configurable permissions</div>
              </div>
            </button>
          </div>
        </div>

        {/* ─── Basic Info ─── */}
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-dark mb-4">Basic Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-dark mb-1.5">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-lime focus:ring-2 focus:ring-lime/30 outline-none transition-all bg-white"
                placeholder="user@company.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-dark mb-1.5">Contact Name</label>
              <input
                type="text"
                required
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-lime focus:ring-2 focus:ring-lime/30 outline-none transition-all bg-white"
                placeholder="Full name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-dark mb-1.5">Company Name</label>
              <input
                type="text"
                required
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-lime focus:ring-2 focus:ring-lime/30 outline-none transition-all bg-white"
                placeholder="Company Ltd."
              />
            </div>
          </div>
        </div>

        {/* ─── Billing & Shipping ─── */}
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-dark">
              Billing & Shipping
            </h2>
            {type === 'employee' && (
              <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded-md">Optional</span>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-dark mb-1.5">VAT Number</label>
              <input
                type="text"
                required={type === 'customer'}
                value={vatNumber}
                onChange={(e) => setVatNumber(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-lime focus:ring-2 focus:ring-lime/30 outline-none transition-all bg-white"
                placeholder="IT12345678901"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-dark mb-1.5">SDI Code</label>
              <input
                type="text"
                required={type === 'customer'}
                value={sdiNumber}
                onChange={(e) => setSdiNumber(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-lime focus:ring-2 focus:ring-lime/30 outline-none transition-all bg-white"
                placeholder="0000000"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-dark mb-1.5">Street Address</label>
              <input
                type="text"
                required={type === 'customer'}
                value={street}
                onChange={(e) => setStreet(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-lime focus:ring-2 focus:ring-lime/30 outline-none transition-all bg-white"
                placeholder="Via Roma 1"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-dark mb-1.5">City</label>
              <input
                type="text"
                required={type === 'customer'}
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-lime focus:ring-2 focus:ring-lime/30 outline-none transition-all bg-white"
                placeholder="Milano"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-dark mb-1.5">Province</label>
              <input
                type="text"
                required={type === 'customer'}
                value={province}
                onChange={(e) => setProvince(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-lime focus:ring-2 focus:ring-lime/30 outline-none transition-all bg-white"
                placeholder="MI"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-dark mb-1.5">Postal Code</label>
              <input
                type="text"
                required={type === 'customer'}
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-lime focus:ring-2 focus:ring-lime/30 outline-none transition-all bg-white"
                placeholder="20100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-dark mb-1.5">Country</label>
              <input
                type="text"
                required={type === 'customer'}
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-lime focus:ring-2 focus:ring-lime/30 outline-none transition-all bg-white"
                placeholder="IT"
              />
            </div>
          </div>
        </div>

        {/* ─── Employee Permissions ─── */}
        {type === 'employee' && (
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-dark mb-1">Permissions</h2>
            <p className="text-sm text-gray-500 mb-4">Select the admin areas this employee can access.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {ASSIGNABLE_PERMISSIONS.map((perm) => (
                <label
                  key={perm.scope}
                  className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                    permissions.includes(perm.scope)
                      ? 'border-indigo-400 bg-indigo-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={permissions.includes(perm.scope)}
                    onChange={() => togglePermission(perm.scope)}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <div>
                    <p className="text-sm font-medium text-dark">{perm.label}</p>
                    <p className="text-xs text-gray-500">{perm.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* ─── Info Banner ─── */}
        <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-xl text-sm flex items-start gap-2">
          <svg className="w-5 h-5 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
          </svg>
          <div>
            <p className="font-medium">How enrollment works</p>
            <ul className="mt-1 space-y-0.5 text-xs text-blue-600">
              <li>A secure random password will be generated automatically.</li>
              <li>The user will be auto-approved (no verification needed).</li>
              <li>On first login, the user will be required to change the temporary password.</li>
            </ul>
          </div>
        </div>

        {/* ─── Submit ─── */}
        <button
          type="submit"
          disabled={loading}
          className="w-full btn-gradient py-3 rounded-xl text-base font-semibold disabled:opacity-60 disabled:cursor-not-allowed active:scale-[0.98] transition-transform"
        >
          {loading ? 'Enrolling...' : `Enroll ${type === 'employee' ? 'Employee' : 'Customer'}`}
        </button>
      </form>
    </div>
  );
}
